import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type CompositionEvent,
  type FocusEvent,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react'
import { countUrlOccurrences, extractCompletedInlineTag, extractInlineTags, extractUrls, type UrlEntity } from '../domain/note'
import { createPortal } from 'react-dom'
import { APP_SETTINGS } from '../config/appSettings'
import { getEditorShortcut, type EditorChangeKind } from '../utils/markdownEditing'
import {
  convertList,
  deleteListItem,
  editorFormatStatesEqual,
  EMPTY_EDITOR_FORMAT_STATE,
  ensureEditableParagraphAfterList,
  ensureChecklistTextEntries,
  ensureTrailingEditableParagraph,
  ensureTrailingCodeBlockExit,
  exitQuote,
  extractListItem,
  formatWordAtCaret,
  getEditorFormatState,
  getListType,
  insertDivider as insertDividerDom,
  insertList,
  insertCodeLineBreakAtRange,
  insertSoftLineBreakAtRange,
  insertPastedEditorHtml,
  indentListItem,
  indentTextAtRange,
  isCaretAtStartOfListItem,
  moveListItem,
  outdentListItem,
  outdentTextAtRange,
  mergeConsecutiveLists,
  normalizeChecklistCaretBoundary,
  nextHeadingTag,
  placeCaretAtEnd as placeCaretAtEndOfEditor,
  placeCaretAtEndOfFirstLine,
  placeCaretAtStartOfListItem,
  placeCaretInListItem,
  reorderChecklistItem,
  splitListItem,
  unwrapCodeBlock,
  unwrapInlineScopeAtCaret,
  wrapBlocksInList,
  type EditorFormatState,
  type ListType,
} from '../utils/editorDom'
import {
  looksLikeMarkdown,
  editorSelectionForClipboard,
  ensureFormattedNoteLinkActions,
  markdownToEditorHtml,
  NOTE_LINK_CARET_ATTRIBUTE,
  NOTE_LINK_OPEN_ATTRIBUTE,
  normalizeInlineTagLinks,
  pastedHtmlToMarkdown,
  serializeRoot,
} from '../utils/markdownWysiwyg'
import { linkTargetKey } from '../links/linkDisplay'
import { decorateLinkUnits } from '../links/linkUnitDecoration'
import type { LinkDisplayChoice } from '../links/linkDisplay'
import { LinkUnitMenu, type EditorLinkActions } from './LinkUnitMenu'
import {
  formatNoteUrlsAsMarkdown,
  healedAutomaticUrlLabel,
  normalizeSubmittedWebUrl,
} from '../utils/urlIntake'
import { Icon } from './Icon'
import { leaveSheet, useLeaveSheet } from './sheetMotion'
import { clipboardImageFiles } from '../assets/clipboardImageFiles'
import { placeCaretPopover } from '../utils/caretPopover'

export type MarkdownFormat =
  | 'bold'
  | 'plain-text'
  | 'italic'
  | 'underline'
  | 'heading'
  | 'divider'
  | 'link'
  | 'highlight'
  | 'checklist'
  | 'ordered-list'
  | 'bullet-list'
  | 'quote'
  | 'code-block'

export interface MarkdownEditorHandle {
  captureListSelection: () => void
  deleteCurrentLine: () => void
  /** Open the link dialog on the link to this address (the card above the note edits its link this way). */
  editLink: (href: string) => void
  focus: (options?: FocusOptions) => void
  findAndSelect: (text: string) => boolean
  format: (format: MarkdownFormat) => void
  placeCaretAtEnd: () => void
  placeCaretAtEndOfFirstLine: () => void
  restoreSelection: (options?: FocusOptions) => void
}

interface MarkdownEditorProps {
  initialFocus?: 'default' | 'first-line-end' | 'none'
  markdown: string
  onChange: (markdown: string, kind: EditorChangeKind) => void
  onDone: () => void
  onFormatStateChange: (state: EditorFormatState) => void
  onEditorFocusChange?: (focused: boolean) => void
  onNewNote: () => void
  onPasteImages?: (files: readonly File[]) => void
  onPasteLinkAttachments?: (urls: readonly UrlEntity[], attach: boolean) => void
  onInlineTags?: (tags: readonly string[]) => void
  inlineTagSuggestions?: readonly string[]
  onRedo: () => void
  onUndo: () => void
  placeholder?: string
  previewUrls?: readonly UrlEntity[]
  /**
   * Target key of a leading link that a card outside the editor shows instead. The link block is hidden
   * but stays in the document and its Markdown, and an empty paragraph below it takes the caret.
   */
  collapseLeadingLinkTarget?: string | null
  /** Re-fetch a link's details; offered from the link menu when they could not be loaded. */
  onRetryLinkMetadata?: (url: UrlEntity) => void
  /** The display switch in a pill's or a row's menu: how the owner chose to show this link (or the large preview). */
  onLinkDisplay?: (url: UrlEntity, choice: LinkDisplayChoice) => void
  /** What the note's screen can do with a link beyond what the editor knows (summary, ask, remove), for its menu. */
  linkActions?: (url: UrlEntity) => EditorLinkActions
  /** Delete this link and its words from the note: offered on a pill's bar and on an opened row, as the card's menu offers it. */
  onRemoveLink?: (url: UrlEntity) => void
}

interface LinkDraft {
  text: string
  url: string
}

interface PendingLinkPaste {
  richHtml: string
  text: string
  urls: UrlEntity[]
  urlCount: number
  range: Range
}

interface ListActionsState {
  canMoveDown: boolean
  canMoveUp: boolean
}

interface EditorSelectionSnapshot {
  endContainer: Node
  endOffset: number
  startContainer: Node
  startOffset: number
}

interface InlineTagAutocompleteState {
  left: number
  query: string
  top: number
}

const EMPTY_PREVIEW_URLS: readonly UrlEntity[] = []

function closestElement(node: Node | null, root: HTMLElement): HTMLElement | null {
  const element = node instanceof HTMLElement ? node : node?.parentElement
  if (!element || !root.contains(element)) return null
  return element
}

function closestBlock(node: Node | null, root: HTMLElement): HTMLElement | null {
  let element = closestElement(node, root)
  while (element && element !== root) {
    if (/^(P|DIV|H[1-6]|LI|BLOCKQUOTE|PRE)$/.test(element.tagName)) return element
    element = element.parentElement
  }
  return null
}

function stripMarkdownSyntax(markdown: string): string {
  return markdown
    .replace(/!\[([^\]\n]*)\]\((?:\\.|[^)])*\)/g, '$1')
    .replace(/\[([^\]\n]*)\]\((?:\\.|[^)])*\)/g, '$1')
    .replace(/<((?:https?:\/\/)[^\s<>]+)>/g, '$1')
    .replace(/<\/?u>/gi, '')
    .split(/\r?\n/)
    .map((line) => {
      if (/^\s{0,3}(?:`{3,}|~{3,})/.test(line)) return ''
      if (/^\s{0,3}(?:[-*_]\s*){3,}$/.test(line)) return ''
      return line
        .replace(/^\s{0,3}#{1,6}\s+/, '')
        .replace(/^\s{0,3}>\s?/, '')
        .replace(/^(\s*)(?:[-+*]|\d+[.)])\s+(?:\[[ xX]\]\s*)?/, '$1')
        .replace(/(\\)([*_`~#[\]{}()<>+\-.!|])/g, '$2')
        .replace(/[=*~`_\\]/g, '')
    })
    .join('\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function placeCaret(element: Node, atStart = false): void {
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  range.selectNodeContents(element)
  range.collapse(atStart)
  selection.removeAllRanges()
  selection.addRange(range)
}

function snapshotEditorSelection(
  root: HTMLElement,
  savedRange: Range | null,
): EditorSelectionSnapshot | null {
  const selection = window.getSelection()
  const currentRange = selection?.rangeCount ? selection.getRangeAt(0) : null
  const range = currentRange && root.contains(currentRange.commonAncestorContainer)
    ? currentRange
    : savedRange && root.contains(savedRange.commonAncestorContainer)
      ? savedRange
      : null
  return range
    ? {
        endContainer: range.endContainer,
        endOffset: range.endOffset,
        startContainer: range.startContainer,
        startOffset: range.startOffset,
      }
    : null
}

function restoreEditorSelection(
  root: HTMLElement,
  snapshot: EditorSelectionSnapshot | null,
): boolean {
  if (
    !snapshot
    || !root.contains(snapshot.startContainer)
    || !root.contains(snapshot.endContainer)
  ) return false
  try {
    const range = document.createRange()
    range.setStart(snapshot.startContainer, snapshot.startOffset)
    range.setEnd(snapshot.endContainer, snapshot.endOffset)
    const selection = window.getSelection()
    if (!selection) return false
    selection.removeAllRanges()
    selection.addRange(range)
    return true
  } catch {
    return false
  }
}

/** The dressed link (pill or row) a node belongs to, or null. */
function dressedLinkUnit(node: Node | null, root: HTMLElement): HTMLElement | null {
  const unit = closestElement(node, root)?.closest<HTMLElement>('[data-note-link-unit]') ?? null
  return unit?.querySelector(':scope > a[data-note-link-display]') ? unit : null
}

/**
 * A pill or row is one object: the caret never rests inside it, wherever the browser put it (its trailing caret span, beside the
 * label, after the open action). A caret that ends up in one is moved to the nearest side of the unit, so text typed there lands
 * in the note's own text, never inside the link.
 */
function keepCaretOutOfLinkDisplays(root: HTMLElement): void {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return
  const range = selection.getRangeAt(0)
  if (!root.contains(range.startContainer)) return
  const unit = dressedLinkUnit(range.startContainer, root)
  const label = unit?.querySelector(':scope > a[data-note-link-display]')
  if (!unit || !label) return
  const labelRange = document.createRange()
  labelRange.selectNode(label)
  const before = range.compareBoundaryPoints(Range.START_TO_START, labelRange) <= 0
  const place = document.createRange()
  if (before) place.setStartBefore(unit); else place.setStartAfter(unit)
  place.collapse(true)
  selection.removeAllRanges()
  selection.addRange(place)
}

/** The link unit directly before a collapsed caret, whether the caret is in its anchor span or just after it. */
function linkUnitBeforeCaret(root: HTMLElement, range: Range): HTMLElement | null {
  if (!range.collapsed) return null
  const container = range.startContainer
  const caretSpan = closestElement(container, root)?.closest<HTMLElement>(`[${NOTE_LINK_CARET_ATTRIBUTE}]`)
  if (caretSpan) {
    const unit = caretSpan.closest<HTMLElement>('[data-note-link-unit]')
    const remainder = document.createRange()
    remainder.selectNodeContents(caretSpan)
    remainder.setStart(container, range.startOffset)
    return unit && !remainder.toString().replace(/\u200B/g, '') ? unit : null
  }
  const previous = container.nodeType === Node.TEXT_NODE
    ? range.startOffset === 0 ? container.previousSibling : null
    : container.childNodes[range.startOffset - 1] ?? null
  return previous instanceof HTMLElement && previous.hasAttribute('data-note-link-unit') ? previous : null
}

/** URL values shown as previews. May also name the one leading link a card above the editor stands in for. */
type PreviewUrlValues = ReadonlySet<string> & {
  collapseLeadingLinkTarget?: string | null
  /** Link details by comparable target, used to dress links as chips and rows. */
  urlByTarget?: ReadonlyMap<string, UrlEntity>
}

const NO_URL_DETAILS: ReadonlyMap<string, UrlEntity> = new Map()

/** The block holds one link and nothing else (the open action and caret anchor carry no text). */
function isOwnLineLinkBlock(block: HTMLElement, targetKey: string): boolean {
  const links = block.querySelectorAll<HTMLAnchorElement>(`a[href]:not([${NOTE_LINK_OPEN_ATTRIBUTE}])`)
  const visible = (node: Node) => (node.textContent ?? '').replace(/\u200B/g, '').trim()
  // A URL typed but not yet turned into a Markdown link is a bare text block.
  if (links.length === 0) return /^https?:\/\/\S+$/i.test(visible(block)) && linkTargetKey(visible(block)) === targetKey
  if (links.length !== 1) return false
  const link = links[0]!
  return linkTargetKey(link.getAttribute('href') ?? '') === targetKey && visible(block) === visible(link)
}

function normalizeEditable(root: HTMLElement, previewUrlValues: PreviewUrlValues = new Set()): void {
  for (const input of root.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
    const item = input.closest('li')
    if (item && (input.parentElement !== item || input !== item.lastChild)) item.append(input)
    input.disabled = false
    input.contentEditable = 'false'
    input.tabIndex = -1
    input.closest('ul')?.setAttribute('data-checklist', 'true')
  }
  ensureChecklistTextEntries(root)
  mergeConsecutiveLists(root)
  for (const list of root.querySelectorAll<HTMLElement>('ul[data-checklist]')) {
    const checkedItems = [...list.children].filter((item): item is HTMLLIElement =>
      item instanceof HTMLLIElement
      && Boolean(item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')?.checked),
    )
    // Moving an already-positioned LI still collapses live DOM ranges out of
    // its text. Keep normalization idempotent while typing/backspacing.
    const selection = snapshotEditorSelection(root, null)
    let moved = false
    let next: Element | null = null
    for (const item of checkedItems.reverse()) {
      if (item.nextElementSibling !== next) {
        list.insertBefore(item, next)
        moved = true
      }
      next = item
    }
    if (moved) restoreEditorSelection(root, selection)
  }
  for (const span of root.querySelectorAll<HTMLSpanElement>('span[style]')) {
    const parent = span.parentNode
    if (!parent) continue
    while (span.firstChild) parent.insertBefore(span.firstChild, span)
    span.remove()
  }
  for (const block of root.querySelectorAll<HTMLElement>('[data-link-preview-source="true"]')) {
    block.removeAttribute('data-link-preview-source')
  }
  if (previewUrlValues.size > 0) {
    for (const block of root.querySelectorAll<HTMLElement>(':scope > p, :scope > div')) {
      const sourceText = (block.textContent ?? '').replace(/\u200B/g, '').trim()
      if (previewUrlValues.has(sourceText)) {
        block.dataset.linkPreviewSource = 'true'
      }
    }
    const leadingTarget = previewUrlValues.collapseLeadingLinkTarget
    const leadingBlock = root.firstElementChild
    if (leadingTarget && leadingBlock instanceof HTMLElement && isOwnLineLinkBlock(leadingBlock, leadingTarget)) {
      leadingBlock.dataset.linkPreviewSource = 'true'
    }
    const hasVisibleBlock = [...root.children].some((block) =>
      !(block instanceof HTMLElement) || block.dataset.linkPreviewSource !== 'true',
    )
    if (!hasVisibleBlock) {
      const continuation = document.createElement('p')
      continuation.dataset.linkPreviewContinuation = 'true'
      continuation.append(document.createElement('br'))
      root.append(continuation)
    }
  }
  ensureTrailingCodeBlockExit(root)
  ensureFormattedNoteLinkActions(root)
  decorateLinkUnits(root, previewUrlValues.urlByTarget ?? NO_URL_DETAILS)
}

function healEditableUrlLabels(
  root: HTMLElement,
  previewUrls: readonly UrlEntity[],
  preserveActiveSelection = true,
): boolean {
  const selection = window.getSelection()
  const range = selection?.rangeCount ? selection.getRangeAt(0) : null
  let changed = false
  for (const anchor of root.querySelectorAll<HTMLAnchorElement>('a[href]')) {
    if (anchor.hasAttribute(NOTE_LINK_OPEN_ATTRIBUTE)) continue
    if (
      preserveActiveSelection
      && document.activeElement === root
      && range
      && anchor.contains(range.commonAncestorContainer)
    ) continue
    const href = anchor.getAttribute('href') ?? ''
    const currentLabel = anchor.textContent ?? ''
    const healedLabel = healedAutomaticUrlLabel(currentLabel, href, previewUrls)
    if (!healedLabel) continue
    anchor.textContent = healedLabel
    changed = true
  }
  return changed
}

function animateChecklistReorder(
  items: readonly HTMLLIElement[],
  previousTops: ReadonlyMap<HTMLLIElement, number>,
): void {
  if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  for (const item of items) {
    const previousTop = previousTops.get(item)
    if (previousTop === undefined || typeof item.animate !== 'function') continue
    const offset = previousTop - item.getBoundingClientRect().top
    if (Math.abs(offset) < 1) continue
    item.animate(
      [{ transform: `translateY(${offset}px)` }, { transform: 'translateY(0)' }],
      { duration: 220, easing: 'cubic-bezier(0.2, 0.75, 0.25, 1)' },
    )
  }
}

function savedRangeInside(root: HTMLElement, saved: Range | null): Range | null {
  const selection = window.getSelection()
  if (selection && selection.rangeCount > 0) {
    const current = selection.getRangeAt(0)
    if (root.contains(current.commonAncestorContainer)) return current
  }
  if (saved && root.contains(saved.commonAncestorContainer)) {
    selection?.removeAllRanges()
    selection?.addRange(saved)
    return saved
  }
  root.focus({ preventScroll: true })
  placeCaret(root)
  return window.getSelection()?.rangeCount ? window.getSelection()!.getRangeAt(0) : null
}

const MarkdownEditorImpl = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(
  function MarkdownEditor(
    {
      initialFocus = 'default',
      markdown,
      onChange,
      onDone,
      onEditorFocusChange,
      onFormatStateChange,
      onNewNote,
      onPasteImages,
      onPasteLinkAttachments,
      onInlineTags,
      inlineTagSuggestions = [],
      onRedo,
      onUndo,
      placeholder = 'Capture anything…',
      previewUrls = EMPTY_PREVIEW_URLS,
      collapseLeadingLinkTarget = null,
      onRetryLinkMetadata,
      onLinkDisplay,
      linkActions,
      onRemoveLink,
    },
    forwardedRef,
  ) {
    const rootRef = useRef<HTMLDivElement>(null)
    const savedRangeRef = useRef<Range | null>(null)
    const listToolbarRangeRef = useRef<Range | null>(null)
    const lastEmittedRef = useRef(markdown)
    const renderedMarkdownRef = useRef<string | null>(null)
    const existingLinkRef = useRef<HTMLAnchorElement | null>(null)
    const linkDialogRef = useRef<HTMLDialogElement>(null)
    const pasteDialogRef = useRef<HTMLDialogElement>(null)
    const linkTitleInputRef = useRef<HTMLInputElement>(null)
    const activeListItemRef = useRef<HTMLLIElement | null>(null)
    const pendingCheckboxSelectionRef = useRef<EditorSelectionSnapshot | null>(null)
    const pasteCaptureCleanupRef = useRef<(() => void) | null>(null)
    const listActionsRef = useRef<HTMLDivElement>(null)
    const listActionsFrameRef = useRef<number | null>(null)
    const listActionsOwnerRef = useRef<HTMLElement | null>(null)
    const refreshListActionsRef = useRef<() => void>(() => undefined)
    const reportedFormatStateRef = useRef<EditorFormatState>(EMPTY_EDITOR_FORMAT_STATE)
    const [linkOpen, setLinkOpen] = useState(false)
    const [menuAnchor, setMenuAnchor] = useState<HTMLAnchorElement | null>(null)
    const closeLinkMenu = useCallback(() => setMenuAnchor(null), [])
    // The link whose menu is open stays marked, so it is clear what the menu is about.
    useEffect(() => {
      const unit = menuAnchor?.closest<HTMLElement>('[data-note-link-unit]')
      unit?.setAttribute('data-menu-open', 'true')
      return () => unit?.removeAttribute('data-menu-open')
    }, [menuAnchor])
    const [pendingLinkPaste, setPendingLinkPaste] = useState<PendingLinkPaste | null>(null)
    const [linkDraft, setLinkDraft] = useState<LinkDraft>({ text: '', url: '' })
    const [linkError, setLinkError] = useState<string | null>(null)
    const [listActions, setListActions] = useState<ListActionsState | null>(null)
    const [listActionsExpanded, setListActionsExpanded] = useState(false)
    const [inlineTagAutocomplete, setInlineTagAutocomplete] = useState<InlineTagAutocompleteState | null>(null)
    const inlineTagListboxId = useId()
    const inlineTagFrameRef = useRef<HTMLDivElement>(null)
    const inlineTagMenuRef = useRef<HTMLDivElement>(null)
    const dismissedInlineTagRef = useRef<{ node: Node; offset: number; query: string } | null>(null)
    const [activeInlineTag, setActiveInlineTag] = useState<{ query: string; tag: string } | null>(null)
    const previewUrlValuesKey = previewUrls
      .flatMap((url) => [url.originalUrl, url.canonicalUrl ?? ''])
      .filter(Boolean)
      .join('\u0000')
    const previewUrlDetailsKey = previewUrls
      .map((url) => [url.originalUrl, url.title, url.imageUrl, url.author, url.pageKind, url.fetchedAt, url.metadataFetchAttemptCount, url.display].join('\u0001'))
      .join('\u0000')
    const previewUrlValues = useMemo(
      () => Object.assign(new Set(previewUrlValuesKey.split('\u0000').filter(Boolean)), {
        collapseLeadingLinkTarget,
        urlByTarget: new Map(previewUrls.flatMap((url) => [url.originalUrl, url.canonicalUrl ?? '']
          .filter(Boolean).map((value) => [linkTargetKey(value), url] as const))),
      }),
      // The details key stands in for previewUrls, which is a new array on every render.
      [previewUrlValuesKey, previewUrlDetailsKey, collapseLeadingLinkTarget],
    )
    const inlineTagMatches = useMemo(() => {
      if (!inlineTagAutocomplete) return []
      return [...new Set(inlineTagSuggestions.map((tag) => tag.trim().toLowerCase()).filter(Boolean))]
        .filter((tag) => tag.includes(inlineTagAutocomplete.query))
        .slice(0, 6)
    }, [inlineTagAutocomplete, inlineTagSuggestions])
    const activeInlineTagIndex = activeInlineTag?.query === inlineTagAutocomplete?.query
      ? inlineTagMatches.indexOf(activeInlineTag?.tag ?? '')
      : -1
    const inlineTagMenuOpen = Boolean(inlineTagAutocomplete && inlineTagMatches.length)

    const dismissInlineTagAutocomplete = useCallback(() => {
      const range = savedRangeRef.current
      if (range && inlineTagAutocomplete) {
        dismissedInlineTagRef.current = {
          node: range.startContainer,
          offset: range.startOffset,
          query: inlineTagAutocomplete.query,
        }
      }
      setInlineTagAutocomplete(null)
      setActiveInlineTag(null)
    }, [inlineTagAutocomplete])

    const clearListActionsOwner = useCallback(() => {
      listActionsOwnerRef.current?.removeAttribute('data-list-actions-owner')
      listActionsOwnerRef.current = null
      rootRef.current?.removeAttribute('data-list-actions-expanded')
    }, [])

    const updateListActionsPosition = useCallback(() => {
      const root = rootRef.current
      const item = activeListItemRef.current
      const actions = listActionsRef.current
      if (!root || !item || !actions || !root.contains(item)) return
      const itemRect = item.getBoundingClientRect()
      const rootRect = root.getBoundingClientRect()
      const scrollHost = root.closest('.composer__scroll-region')
      const visibleRect = scrollHost?.getBoundingClientRect() ?? rootRect
      actions.hidden = itemRect.bottom < visibleRect.top || itemRect.top > visibleRect.bottom
      const actionsHeight = actions.offsetHeight || 48
      const actionsWidth = actions.offsetWidth || 44
      const minimumTop = visibleRect.top + 8
      const maximumTop = Math.max(minimumTop, visibleRect.bottom - actionsHeight - 8)
      const top = `${Math.min(maximumTop, Math.max(minimumTop, visibleRect.top + (visibleRect.height - actionsHeight) / 2))}px`
      const minimumLeft = visibleRect.left + 8
      const maximumLeft = Math.max(minimumLeft, visibleRect.right - actionsWidth - 8)
      const left = `${Math.min(maximumLeft, Math.max(minimumLeft, rootRect.right - actionsWidth - 2))}px`
      actions.style.top = top
      actions.style.left = left
    }, [])

    const scheduleListActionsPosition = useCallback(() => {
      if (listActionsFrameRef.current !== null) return
      listActionsFrameRef.current = requestAnimationFrame(() => {
        listActionsFrameRef.current = null
        updateListActionsPosition()
      })
    }, [updateListActionsPosition])

    const refreshListActions = useCallback(() => {
      const root = rootRef.current
      const selection = window.getSelection()
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null
      const candidate = root && range && root.contains(range.commonAncestorContainer)
        ? closestElement(range.startContainer, root)?.closest('li')
        : null
      const item = candidate?.parentElement && getListType(candidate.parentElement) ? candidate : null
      const previous = activeListItemRef.current
      if (previous !== item) {
        const nextList = item?.parentElement
        previous?.removeAttribute('data-list-actions-active')
        activeListItemRef.current = item
        // The expanded helper has a stable owner list. Its trigger can come and go
        // with the caret, but only a different list (or non-list content) closes the
        // current session.
        const owner = listActionsOwnerRef.current
        if (listActionsExpanded && owner && nextList !== owner) {
          setListActionsExpanded(false)
        }
        if (listActionsExpanded && nextList === owner) {
          item?.setAttribute('data-list-actions-active', 'true')
        }
      }
      const next = item
        ? {
            canMoveDown: item.nextElementSibling instanceof HTMLLIElement,
            canMoveUp: item.previousElementSibling instanceof HTMLLIElement,
          }
        : null
      setListActions((current) =>
        current?.canMoveDown === next?.canMoveDown && current?.canMoveUp === next?.canMoveUp
          ? current
          : next,
      )
      if (item) scheduleListActionsPosition()

      let formatState = root && range && root.contains(range.commonAncestorContainer)
        ? getEditorFormatState(root, range, selection?.focusNode)
        : EMPTY_EDITOR_FORMAT_STATE
      if (range?.collapsed && formatState !== EMPTY_EDITOR_FORMAT_STATE) {
        try {
          formatState = {
            ...formatState,
            bold: formatState.bold || document.queryCommandState('bold'),
            italic: formatState.italic || document.queryCommandState('italic'),
            underline: formatState.underline || document.queryCommandState('underline'),
          }
        } catch {
          // DOM ancestry remains the source of truth when command-state APIs are unavailable.
        }
      }
      if (!editorFormatStatesEqual(reportedFormatStateRef.current, formatState)) {
        reportedFormatStateRef.current = formatState
        onFormatStateChange(formatState)
      }
    }, [listActionsExpanded, onFormatStateChange, scheduleListActionsPosition])

    const refreshInlineTagAutocomplete = useCallback(() => {
      const root = rootRef.current
      const selection = window.getSelection()
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null
      if (!root || !range?.collapsed || !root.contains(range.startContainer)
        || (!root.contains(document.activeElement) && !inlineTagMenuRef.current?.contains(document.activeElement))) {
        setInlineTagAutocomplete(null)
        return
      }
      const block = closestBlock(range.startContainer, root) ?? root
      if (!block.contains(range.startContainer)) {
        setInlineTagAutocomplete(null)
        return
      }
      const prefix = document.createRange()
      prefix.selectNodeContents(block)
      prefix.setEnd(range.startContainer, range.startOffset)
      const query = /(?:^|\s)#([a-z0-9][a-z0-9_-]*)$/i.exec(prefix.toString())?.[1]?.toLowerCase()
      if (!query) {
        dismissedInlineTagRef.current = null
        setInlineTagAutocomplete(null)
        return
      }
      const dismissed = dismissedInlineTagRef.current
      if (dismissed?.node === range.startContainer && dismissed.offset === range.startOffset && dismissed.query === query) {
        setInlineTagAutocomplete(null)
        return
      }
      dismissedInlineTagRef.current = null
      const rect = typeof range.getBoundingClientRect === 'function'
        ? range.getBoundingClientRect()
        : root.getBoundingClientRect()
      const next = { left: rect.left, query, top: rect.bottom }
      setInlineTagAutocomplete((current) => (
        current?.query === next.query && current.left === next.left && current.top === next.top
          ? current
          : next
      ))
    }, [])

    const updateInlineTagPosition = useCallback(() => {
      const frame = inlineTagFrameRef.current
      const menu = inlineTagMenuRef.current
      const range = savedRangeRef.current
      const root = rootRef.current
      if (!frame || !menu || !range || !root?.contains(range.startContainer)) return
      const caret = typeof range.getBoundingClientRect === 'function'
        ? range.getBoundingClientRect()
        : root.getBoundingClientRect()
      const bounds = frame.getBoundingClientRect()
      menu.hidden = caret.bottom < bounds.top || caret.top > bounds.bottom
      const position = placeCaretPopover(caret, bounds, {
        width: menu.offsetWidth,
        height: menu.scrollHeight + menu.offsetHeight - menu.clientHeight,
      })
      menu.style.left = `${position.left}px`
      menu.style.top = `${position.top}px`
      menu.style.maxHeight = `${position.maxHeight}px`
    }, [])

    useLayoutEffect(() => {
      if (!inlineTagMenuOpen) return
      updateInlineTagPosition()
      // Observe the frame driven by the central viewport CSS variables, never
      // install another window/visualViewport geometry listener for this menu.
      const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateInlineTagPosition)
      if (inlineTagFrameRef.current) observer?.observe(inlineTagFrameRef.current)
      if (inlineTagMenuRef.current) observer?.observe(inlineTagMenuRef.current)
      if (rootRef.current) observer?.observe(rootRef.current)
      const scrollHost = rootRef.current?.closest('.composer__scroll-region')
      scrollHost?.addEventListener('scroll', updateInlineTagPosition, { passive: true })
      return () => {
        observer?.disconnect()
        scrollHost?.removeEventListener('scroll', updateInlineTagPosition)
      }
    }, [inlineTagAutocomplete, inlineTagMenuOpen, updateInlineTagPosition])

    useLayoutEffect(() => {
      const menu = inlineTagMenuRef.current
      const option = menu?.querySelector<HTMLElement>('[aria-selected="true"]')
      if (!menu || !option) return
      const menuRect = menu.getBoundingClientRect()
      const optionRect = option.getBoundingClientRect()
      if (optionRect.top < menuRect.top) menu.scrollTop -= menuRect.top - optionRect.top
      else if (optionRect.bottom > menuRect.bottom) menu.scrollTop += optionRect.bottom - menuRect.bottom
    }, [activeInlineTagIndex])

    const rememberSelection = useCallback(() => {
      const root = rootRef.current
      const selection = window.getSelection()
      if (!root || !selection || selection.rangeCount === 0) return
      const currentRange = selection.getRangeAt(0)
      const range = root.contains(currentRange.commonAncestorContainer)
        ? normalizeChecklistCaretBoundary(root, currentRange) ?? currentRange
        : currentRange
      if (root.contains(range.commonAncestorContainer)) savedRangeRef.current = range.cloneRange()
      refreshListActions()
      refreshInlineTagAutocomplete()
    }, [refreshInlineTagAutocomplete, refreshListActions])

    const openListActions = useCallback(() => {
      const item = activeListItemRef.current
      const list = item?.parentElement
      if (!item || !list || !getListType(list)) return
      clearListActionsOwner()
      listActionsOwnerRef.current = list
      list.setAttribute('data-list-actions-owner', 'true')
      rootRef.current?.setAttribute('data-list-actions-expanded', 'true')
      setListActionsExpanded(true)
    }, [clearListActionsOwner])

    const emitChange = useCallback(
      (kind: EditorChangeKind) => {
        const root = rootRef.current
        if (!root) return
        normalizeEditable(root, previewUrlValues)
        const next = serializeRoot(root)
        if (next === lastEmittedRef.current) return
        lastEmittedRef.current = next
        renderedMarkdownRef.current = next
        onChange(next, kind)
      },
      [onChange, previewUrlValues],
    )

    useLayoutEffect(() => {
      const root = rootRef.current
      if (!root) return
      if (markdown !== renderedMarkdownRef.current) {
        const normalizedInlineTagLinks = normalizeInlineTagLinks(markdown)
        const renderedMarkdown = normalizedInlineTagLinks.markdown
        lastEmittedRef.current = renderedMarkdown
        renderedMarkdownRef.current = renderedMarkdown
        activeListItemRef.current?.removeAttribute('data-list-actions-active')
        activeListItemRef.current = null
        clearListActionsOwner()
        setListActions(null)
        setListActionsExpanded(false)
        reportedFormatStateRef.current = EMPTY_EDITOR_FORMAT_STATE
        onFormatStateChange(EMPTY_EDITOR_FORMAT_STATE)
        root.innerHTML = markdownToEditorHtml(renderedMarkdown)
        if (renderedMarkdown !== markdown) {
          onInlineTags?.(normalizedInlineTagLinks.tags)
          onChange(renderedMarkdown, 'format')
        }
      }
      normalizeEditable(root, previewUrlValues)
      if (healEditableUrlLabels(root, previewUrls)) emitChange('format')
      ensureTrailingEditableParagraph(root)
    }, [emitChange, markdown, onChange, onFormatStateChange, onInlineTags, previewUrlValues, previewUrls])

    useLayoutEffect(() => {
      refreshListActionsRef.current = refreshListActions
    }, [refreshListActions])

    useLayoutEffect(() => {
      const root = rootRef.current
      if (!root) return
      if (initialFocus === 'none') {
        refreshListActionsRef.current()
        return
      }
      root.focus({ preventScroll: true })
      if (initialFocus === 'first-line-end') placeCaretAtEndOfFirstLine(root)
      else placeCaretAtEndOfEditor(root)
      refreshListActionsRef.current()
    }, [initialFocus])

    useEffect(() => {
      let composing = false
      const handleSelectionChange = () => {
        const root = rootRef.current
        if (root && !composing) keepCaretOutOfLinkDisplays(root)
        rememberSelection()
      }
      const startComposing = () => { composing = true }
      const endComposing = () => { composing = false }
      document.addEventListener('selectionchange', handleSelectionChange)
      document.addEventListener('compositionstart', startComposing, true)
      document.addEventListener('compositionend', endComposing, true)
      return () => {
        document.removeEventListener('selectionchange', handleSelectionChange)
        document.removeEventListener('compositionstart', startComposing, true)
        document.removeEventListener('compositionend', endComposing, true)
      }
    }, [rememberSelection])

    useEffect(() => {
      if (!listActionsExpanded) return
      const handlePointerDownOutsideActiveList = (event: globalThis.PointerEvent) => {
        const list = listActionsOwnerRef.current
        const target = event.target
        if (!(target instanceof Node) || !list) return
        if (list.contains(target) || listActionsRef.current?.contains(target)) return
        setListActionsExpanded(false)
      }
      document.addEventListener('pointerdown', handlePointerDownOutsideActiveList, true)
      return () => document.removeEventListener('pointerdown', handlePointerDownOutsideActiveList, true)
    }, [listActionsExpanded])

    useEffect(() => {
      const item = activeListItemRef.current
      if (listActionsExpanded) {
        const list = listActionsOwnerRef.current ?? item?.parentElement
        if (!list || !getListType(list)) {
          setListActionsExpanded(false)
          return
        }
        listActionsOwnerRef.current = list
        list.setAttribute('data-list-actions-owner', 'true')
        rootRef.current?.setAttribute('data-list-actions-expanded', 'true')
        item?.setAttribute('data-list-actions-active', 'true')
      } else {
        item?.removeAttribute('data-list-actions-active')
        clearListActionsOwner()
      }
    }, [clearListActionsOwner, listActionsExpanded])

    useEffect(() => {
      const root = rootRef.current
      const scrollHost = root?.closest('.composer__scroll-region')
      scrollHost?.addEventListener('scroll', scheduleListActionsPosition, { passive: true })
      window.addEventListener('resize', scheduleListActionsPosition, { passive: true })
      window.visualViewport?.addEventListener('resize', scheduleListActionsPosition, { passive: true })
      window.visualViewport?.addEventListener('scroll', scheduleListActionsPosition, { passive: true })
      return () => {
        scrollHost?.removeEventListener('scroll', scheduleListActionsPosition)
        window.removeEventListener('resize', scheduleListActionsPosition)
        window.visualViewport?.removeEventListener('resize', scheduleListActionsPosition)
        window.visualViewport?.removeEventListener('scroll', scheduleListActionsPosition)
        if (listActionsFrameRef.current !== null) cancelAnimationFrame(listActionsFrameRef.current)
      }
    }, [scheduleListActionsPosition])

    useLayoutEffect(() => {
      if (listActions) updateListActionsPosition()
    }, [listActions, listActionsExpanded, updateListActionsPosition])

    const ensureRange = useCallback(() => {
      const root = rootRef.current
      return root ? savedRangeInside(root, savedRangeRef.current) : null
    }, [])

    const applyInlineTagSuggestion = useCallback((tag: string) => {
      const root = rootRef.current
      const range = ensureRange()
      if (!root || !range?.collapsed || !(range.startContainer instanceof Text)) return
      const beforeCaret = range.startContainer.data.slice(0, range.startOffset)
      const match = /(?:^|\s)#([a-z0-9][a-z0-9_-]*)$/i.exec(beforeCaret)
      if (!match) return
      const markerOffset = match.index + (match[0]!.startsWith('#') ? 0 : 1)
      // Chromium drops a collapsible DOM-inserted trailing space on the next
      // keystroke. Match its native contenteditable space representation.
      const replacement = `#${tag}\u00a0`
      range.startContainer.replaceData(markerOffset, range.startOffset - markerOffset, replacement)
      const nextRange = document.createRange()
      nextRange.setStart(range.startContainer, markerOffset + replacement.length)
      nextRange.collapse(true)
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(nextRange)
      savedRangeRef.current = nextRange.cloneRange()
      setInlineTagAutocomplete(null)
      setActiveInlineTag(null)
      onInlineTags?.([tag])
      rememberSelection()
      emitChange('typing')
      root.focus({ preventScroll: true })
    }, [emitChange, ensureRange, onInlineTags, rememberSelection])

    const toggleInlineCommand = useCallback((format: 'bold' | 'italic' | 'underline') => {
      const root = rootRef.current
      const range = ensureRange()
      if (!root || !range) return
      const selector = format === 'bold' ? 'strong, b' : format === 'italic' ? 'em, i' : 'u'
      const tagName = format === 'bold' ? 'strong' : format === 'italic' ? 'em' : 'u'
      const scope = closestElement(range.startContainer, root)?.closest(selector) as HTMLElement | null
      if (range.collapsed && scope && root.contains(scope)) {
        unwrapInlineScopeAtCaret(scope, range)
      } else if (range.collapsed && formatWordAtCaret(range, tagName)) {
        // The word helper also restores the collapsed caret at its original character.
      } else {
        document.execCommand(format)
      }
      rememberSelection()
      emitChange('format')
    }, [emitChange, ensureRange, rememberSelection])

    const replaceCurrentBlock = useCallback(
      (tag: 'p' | 'blockquote') => {
        const root = rootRef.current
        const range = ensureRange()
        if (!root || !range) return
        const block = closestBlock(range.startContainer, root)
        const target = block?.tagName === tag.toUpperCase() ? 'p' : tag
        document.execCommand('formatBlock', false, target)
        rememberSelection()
        emitChange('format')
      },
      [emitChange, ensureRange, rememberSelection],
    )

    const cycleHeading = useCallback(() => {
      const root = rootRef.current
      const range = ensureRange()
      if (!root || !range) return
      const block = closestBlock(range.startContainer, root)
      document.execCommand('formatBlock', false, nextHeadingTag(block?.tagName))
      rememberSelection()
      emitChange('format')
    }, [emitChange, ensureRange, rememberSelection])

    const toggleCodeBlock = useCallback(() => {
      const root = rootRef.current
      const range = ensureRange()
      if (!root || !range) return
      const activeCodeBlock = closestElement(range.startContainer, root)?.closest('pre')
      if (activeCodeBlock && root.contains(activeCodeBlock)) {
        savedRangeRef.current = unwrapCodeBlock(activeCodeBlock as HTMLElement, range)
      } else {
        document.execCommand('formatBlock', false, 'pre')
      }
      rememberSelection()
      emitChange('format')
    }, [emitChange, ensureRange, rememberSelection])

    const toggleHighlight = useCallback(() => {
      const root = rootRef.current
      const range = ensureRange()
      if (!root || !range) return
      const selectedElement = closestElement(range.startContainer, root)
      const activeMark = selectedElement?.closest('mark')
      if (activeMark && root.contains(activeMark)) {
        if (range.collapsed) {
          unwrapInlineScopeAtCaret(activeMark as HTMLElement, range)
        } else {
          const parent = activeMark.parentNode
          if (!parent) return
          while (activeMark.firstChild) parent.insertBefore(activeMark.firstChild, activeMark)
          activeMark.remove()
        }
      } else if (range.collapsed) {
        if (!formatWordAtCaret(range, 'mark')) {
          const mark = document.createElement('mark')
          mark.textContent = 'highlight'
          range.insertNode(mark)
          const selection = window.getSelection()
          const text = mark.firstChild
          if (selection && text) {
            const selected = document.createRange()
            selected.selectNodeContents(text)
            selection.removeAllRanges()
            selection.addRange(selected)
          }
        }
      } else {
        const mark = document.createElement('mark')
        mark.append(range.extractContents())
        range.insertNode(mark)
        const selection = window.getSelection()
        const selected = document.createRange()
        selected.selectNodeContents(mark)
        selection?.removeAllRanges()
        selection?.addRange(selected)
      }
      rememberSelection()
      emitChange('format')
    }, [emitChange, ensureRange, rememberSelection])

    const insertDivider = useCallback(() => {
      const root = rootRef.current
      const range = ensureRange()
      if (!root || !range) return
      const paragraph = insertDividerDom(root, closestBlock(range.startContainer, root))
      placeCaret(paragraph, true)
      rememberSelection()
      emitChange('format')
    }, [emitChange, ensureRange, rememberSelection])

    const applyListFormat = useCallback((target: ListType) => {
      const root = rootRef.current
      const captured = listToolbarRangeRef.current
      listToolbarRangeRef.current = null
      const range = root && captured && root.contains(captured.commonAncestorContainer)
        ? captured
        : ensureRange()
      if (!root || !range) return
      if (captured === range) {
        const selection = window.getSelection()
        selection?.removeAllRanges()
        selection?.addRange(range)
      }
      const block = closestBlock(range.startContainer, root)
      const endBlock = closestBlock(range.endContainer, root)
      const spansBlocks = Boolean(endBlock && endBlock !== block)
      const item = block?.closest('li') as HTMLLIElement | null
      const currentList = item?.parentElement
      const currentType = currentList ? getListType(currentList) : null

      if (item && currentList && currentType === target) {
        const paragraph = extractListItem(item)
        placeCaret(paragraph)
      } else if (item && currentList && currentType) {
        convertList(currentList, target)
        normalizeEditable(root, previewUrlValues)
        placeCaretInListItem(item)
      } else if (spansBlocks) {
        const selectedItem = block && endBlock
          ? wrapBlocksInList(root, block, endBlock, target)
          : null
        if (selectedItem) {
          normalizeEditable(root, previewUrlValues)
          placeCaretInListItem(selectedItem)
        } else {
          document.execCommand(target === 'ordered' ? 'insertOrderedList' : 'insertUnorderedList')
          const selection = window.getSelection()
          const nativeItem = selection?.rangeCount
            ? closestElement(selection.getRangeAt(0).startContainer, root)?.closest('li') as HTMLLIElement | null
            : null
          const list = nativeItem?.parentElement
          if (list && getListType(list) && getListType(list) !== target) convertList(list, target)
          if (nativeItem) placeCaretInListItem(nativeItem)
        }
      } else {
        const insertedItem = insertList(root, block, range, target)
        normalizeEditable(root, previewUrlValues)
        placeCaretInListItem(insertedItem)
      }
      rememberSelection()
      emitChange('format')
    }, [emitChange, ensureRange, previewUrlValues, rememberSelection])

    const reorderActiveListItem = useCallback((direction: -1 | 1) => {
      const item = activeListItemRef.current
      if (!item || !moveListItem(item, direction)) return
      placeCaretInListItem(item)
      rememberSelection()
      emitChange('format')
      scheduleListActionsPosition()
    }, [emitChange, rememberSelection, scheduleListActionsPosition])

    const deleteActiveListItem = useCallback(() => {
      const root = rootRef.current
      const item = activeListItemRef.current
      if (!root || !item) return
      const nextTarget = deleteListItem(item)
      item.removeAttribute('data-list-actions-active')
      activeListItemRef.current = null
      setListActions(null)
      setListActionsExpanded(false)
      root.focus({ preventScroll: true })
      if (nextTarget instanceof HTMLLIElement) placeCaretInListItem(nextTarget)
      else if (nextTarget) placeCaret(nextTarget, true)
      rememberSelection()
      emitChange('format')
    }, [emitChange, rememberSelection])

    const openLinkDialog = useCallback(() => {
      const root = rootRef.current
      const range = ensureRange()
      if (!root || !range) return
      savedRangeRef.current = range.cloneRange()
      const element = closestElement(range.commonAncestorContainer, root)
      const link = element?.closest('a') as HTMLAnchorElement | null
      existingLinkRef.current = link && root.contains(link) ? link : null
      setLinkDraft({
        text: existingLinkRef.current?.textContent ?? range.toString().replace(/\u200B/g, ''),
        url: existingLinkRef.current?.getAttribute('href') ?? '',
      })
      setLinkError(null)
      setLinkOpen(true)
    }, [ensureRange])

    /** Delete a pill's or a row's link, and its words, from the note. A link without details yet is found from its address alone. */
    const removeLinkAnchor = useCallback((anchor: HTMLAnchorElement) => {
      const href = anchor.getAttribute('href') ?? ''
      const entity = previewUrlValues.urlByTarget?.get(linkTargetKey(href)) ?? extractUrls(href)[0]
      if (entity) { setMenuAnchor(null); onRemoveLink?.(entity) }
    }, [onRemoveLink, previewUrlValues])

    /** The link menu's Edit action: the same link dialog, opened on an existing link. */
    const editLinkAnchor = useCallback((anchor: HTMLAnchorElement) => {
      const root = rootRef.current
      if (!root || !root.contains(anchor)) return
      existingLinkRef.current = anchor
      savedRangeRef.current = null
      setLinkDraft({
        text: (anchor.textContent ?? '').replace(/\u200B/g, ''),
        url: anchor.getAttribute('href') ?? '',
      })
      setLinkError(null)
      setLinkOpen(true)
    }, [])

    const closeLinkDialog = useCallback(() => {
      setLinkError(null)
      setLinkOpen(false)
    }, [])
    // The link editor's own controls close it through the sheet exit; applying a link removes it at once.
    const leaveLinkDialog = useLeaveSheet(linkDialogRef, closeLinkDialog)

    useLayoutEffect(() => {
      if (!linkOpen) return
      const dialog = linkDialogRef.current
      if (!dialog) return
      if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal()
      else dialog.setAttribute('open', '')
      linkTitleInputRef.current?.focus({ preventScroll: true })
      linkTitleInputRef.current?.select()
      return () => {
        if (!dialog.open) return
        if (typeof dialog.close === 'function') dialog.close()
        else dialog.removeAttribute('open')
      }
    }, [linkOpen])

    const submitLink = useCallback(
      (event: FormEvent) => {
        event.preventDefault()
        const root = rootRef.current
        const rawUrl = linkDraft.url.trim()
        if (!root) return
        if (!rawUrl) {
          setLinkError('Enter a URL.')
          return
        }
        const url = normalizeSubmittedWebUrl(rawUrl)
        if (!url) {
          setLinkError('Enter a valid URL.')
          return
        }
        setLinkDraft((current) => ({ ...current, url }))
        const label = linkDraft.text.trim() || url
        const existing = existingLinkRef.current
        if (existing && root.contains(existing)) {
          existing.textContent = label
          existing.setAttribute('href', url)
          const afterLabel = existing.nextElementSibling
          placeCaret(afterLabel instanceof HTMLElement && afterLabel.hasAttribute(NOTE_LINK_CARET_ATTRIBUTE) ? afterLabel : existing)
        } else {
          const range = savedRangeInside(root, savedRangeRef.current)
          if (!range) return
          range.deleteContents()
          const link = document.createElement('a')
          link.setAttribute('href', url)
          link.textContent = label
          range.insertNode(link)
          placeCaret(link)
        }
        existingLinkRef.current = null
        setLinkOpen(false)
        rememberSelection()
        emitChange('format')
      },
      [emitChange, linkDraft, rememberSelection],
    )

    const removeLink = useCallback(() => {
      const root = rootRef.current
      const link = existingLinkRef.current
      if (!root || !link || !root.contains(link) || !link.parentNode) return
      const parent = link.parentNode
      const first = link.firstChild
      while (link.firstChild) parent.insertBefore(link.firstChild, link)
      link.remove()
      if (first) placeCaret(first)
      existingLinkRef.current = null
      setLinkOpen(false)
      rememberSelection()
      emitChange('format')
    }, [emitChange, rememberSelection])

    const applyFormat = useCallback(
      (format: MarkdownFormat) => {
        if (format === 'bold') toggleInlineCommand('bold')
        else if (format === 'plain-text') {
          const root = rootRef.current
          if (!root) return
          const plainText = stripMarkdownSyntax(serializeRoot(root))
          root.innerHTML = markdownToEditorHtml(plainText)
          normalizeEditable(root, previewUrlValues)
          root.focus({ preventScroll: true })
          placeCaretAtEndOfEditor(root)
          rememberSelection()
          emitChange('format')
        }
        else if (format === 'italic') toggleInlineCommand('italic')
        else if (format === 'underline') toggleInlineCommand('underline')
        else if (format === 'heading') cycleHeading()
        else if (format === 'divider') insertDivider()
        else if (format === 'link') openLinkDialog()
        else if (format === 'highlight') toggleHighlight()
        else if (format === 'checklist') applyListFormat('checklist')
        else if (format === 'ordered-list') applyListFormat('ordered')
        else if (format === 'bullet-list') applyListFormat('bullet')
        else if (format === 'quote') replaceCurrentBlock('blockquote')
        else if (format === 'code-block') toggleCodeBlock()
      },
      [
        applyListFormat,
        cycleHeading,
        insertDivider,
        openLinkDialog,
        previewUrlValues,
        replaceCurrentBlock,
        toggleCodeBlock,
        toggleHighlight,
        toggleInlineCommand,
      ],
    )

    const deleteCurrentLine = useCallback(() => {
      const root = rootRef.current
      const range = root ? savedRangeInside(root, savedRangeRef.current) : null
      if (!root || !range) return

      const block = closestBlock(range.startContainer, root)
      if (!block || block === root) return

      const listItem = block.closest('li') as HTMLLIElement | null
      if (listItem && root.contains(listItem)) {
        const nextTarget = deleteListItem(listItem)
        root.focus({ preventScroll: true })
        if (nextTarget instanceof HTMLLIElement) placeCaretInListItem(nextTarget)
        else if (nextTarget) placeCaret(nextTarget, true)
      } else {
        const nextBlock = block.nextElementSibling instanceof HTMLElement ? block.nextElementSibling : null
        const previousBlock = block.previousElementSibling instanceof HTMLElement ? block.previousElementSibling : null
        if (!nextBlock && !previousBlock) {
          block.replaceChildren(document.createElement('br'))
          root.focus({ preventScroll: true })
          placeCaret(block, true)
        } else {
          block.remove()
          root.focus({ preventScroll: true })
          placeCaret(nextBlock ?? previousBlock!, true)
        }
      }

      normalizeEditable(root, previewUrlValues)
      rememberSelection()
      emitChange('typing')
    }, [emitChange, previewUrlValues, rememberSelection])

    useImperativeHandle(
      forwardedRef,
      () => ({
        captureListSelection: () => {
          const root = rootRef.current
          const selection = window.getSelection()
          const range = selection?.rangeCount ? selection.getRangeAt(0) : null
          listToolbarRangeRef.current = root && range && root.contains(range.commonAncestorContainer)
            ? range.cloneRange()
            : savedRangeRef.current?.cloneRange() ?? null
        },
        deleteCurrentLine,
        editLink: (href) => {
          const target = linkTargetKey(href)
          const anchor = [...(rootRef.current?.querySelectorAll<HTMLAnchorElement>('a[href]:not([data-note-link-open])') ?? [])].find((candidate) => linkTargetKey(candidate.getAttribute('href') ?? '') === target)
          if (anchor) editLinkAnchor(anchor)
        },
        focus: (options) => rootRef.current?.focus(options),
        format: applyFormat,
        placeCaretAtEnd: () => {
          const root = rootRef.current
          if (!root) return
          root.focus({ preventScroll: true })
          placeCaretAtEndOfEditor(root)
          rememberSelection()
        },
        placeCaretAtEndOfFirstLine: () => {
          const root = rootRef.current
          if (!root) return
          root.focus({ preventScroll: true })
          placeCaretAtEndOfFirstLine(root)
          rememberSelection()
        },
        findAndSelect: (text) => {
          const root = rootRef.current
          if (!root || !text) return false
          const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
          const nodes: Text[] = []
          let searchable = ''
          while (walker.nextNode()) {
            const node = walker.currentNode as Text
            nodes.push(node)
            searchable += node.data
          }
          const start = searchable.toLocaleLowerCase().indexOf(text.toLocaleLowerCase())
          if (start < 0) return false
          const end = start + text.length
          let offset = 0
          let startNode: Text | null = null
          let endNode: Text | null = null
          let startOffset = 0
          let endOffset = 0
          for (const node of nodes) {
            const next = offset + node.length
            if (!startNode && start <= next) {
              startNode = node
              startOffset = Math.max(0, start - offset)
            }
            if (end <= next) {
              endNode = node
              endOffset = Math.max(0, end - offset)
              break
            }
            offset = next
          }
          if (!startNode || !endNode) return false
          const range = document.createRange()
          range.setStart(startNode, startOffset)
          range.setEnd(endNode, endOffset)
          const selection = window.getSelection()
          selection?.removeAllRanges()
          selection?.addRange(range)
          savedRangeRef.current = range.cloneRange()
          return true
        },
        restoreSelection: (options) => {
          const root = rootRef.current
          if (!root) return
          const snapshot = snapshotEditorSelection(root, savedRangeRef.current)
          root.focus(options ?? { preventScroll: true })
          if (!restoreEditorSelection(root, snapshot)) placeCaretAtEndOfEditor(root)
          rememberSelection()
        },
      }),
      [applyFormat, deleteCurrentLine, editLinkAnchor, rememberSelection],
    )

    const transformTypedMarker = useCallback(() => {
      const root = rootRef.current
      const selection = window.getSelection()
      if (!root || !selection || selection.rangeCount === 0) return false
      const range = selection.getRangeAt(0)
      const detectedBlock = closestBlock(range.startContainer, root)
      const block = detectedBlock ?? (range.startContainer.parentNode === root ? root : null)
      if (!block) return false
      const replaceBlock = (replacement: HTMLElement) => {
        if (block === root) root.replaceChildren(replacement)
        else block.replaceWith(replacement)
      }
      const text = (block.textContent ?? '').replace(/\u00a0/g, ' ')
      const existingBulletItem = block instanceof HTMLLIElement ? block : null
      const existingBulletList = existingBulletItem?.parentElement ?? null
      const inlineChecklist = /^\[([ xX])\] $/.exec(text)
      if (
        inlineChecklist &&
        existingBulletItem &&
        existingBulletList?.tagName === 'UL' &&
        !existingBulletList.hasAttribute('data-checklist')
      ) {
        existingBulletItem.replaceChildren()
        existingBulletList.setAttribute('data-checklist', 'true')
        const input = document.createElement('input')
        input.type = 'checkbox'
        input.checked = inlineChecklist[1]!.toLowerCase() === 'x'
        existingBulletItem.append(document.createElement('br'), input)
        normalizeEditable(root, previewUrlValues)
        placeCaretInListItem(existingBulletItem)
        rememberSelection()
        emitChange('typing')
        return true
      }
      if (block !== root && !['P', 'DIV'].includes(block.tagName)) return false
      const checklist = /^[-*+] \[([ xX])\] $/.exec(text)
      const ordered = /^(\d+)\. $/.exec(text)
      const bullet = /^[-*+] $/.exec(text)
      const heading = /^(#{1,6}) $/.exec(text)
      let replacement: HTMLElement | null = null

      if (checklist || ordered || bullet) {
        const list = document.createElement(ordered ? 'ol' : 'ul')
        if (ordered) list.setAttribute('start', String(Number.parseInt(ordered[1]!, 10)))
        const item = document.createElement('li')
        if (checklist) {
          list.setAttribute('data-checklist', 'true')
          const input = document.createElement('input')
          input.type = 'checkbox'
          input.checked = checklist[1]!.toLowerCase() === 'x'
          item.append(document.createElement('br'), input)
        } else {
          item.append(document.createElement('br'))
        }
        list.append(item)
        replacement = list
        replaceBlock(list)
        normalizeEditable(root, previewUrlValues)
        placeCaretInListItem(item)
      } else if (heading) {
        replacement = document.createElement(`h${heading[1]!.length}`)
        replacement.append(document.createElement('br'))
        replaceBlock(replacement)
        placeCaret(replacement, true)
      } else if (text === '> ') {
        replacement = document.createElement('blockquote')
        replacement.append(document.createElement('br'))
        replaceBlock(replacement)
        placeCaret(replacement, true)
      } else if (text === '```') {
        replacement = document.createElement('pre')
        replacement.append(document.createElement('br'))
        replaceBlock(replacement)
        placeCaret(replacement, true)
      } else if (text === '---') {
        block.replaceChildren()
        const paragraph = insertDividerDom(root, block === root ? null : block)
        replacement = paragraph
        placeCaret(paragraph, true)
      }
      if (!replacement) return false
      rememberSelection()
      emitChange('typing')
      return true
    }, [emitChange, previewUrlValues, rememberSelection])

    const deleteEmptyChecklistItem = useCallback((item: HTMLLIElement, backward: boolean) => {
      const root = rootRef.current
      const list = item.parentElement
      if (
        !root
        || list?.tagName !== 'UL'
        || !list.hasAttribute('data-checklist')
        || item.textContent?.replace(/\u200B/g, '').trim()
      ) return false

      const previousItem = item.previousElementSibling instanceof HTMLLIElement
        ? item.previousElementSibling
        : null
      const nextTarget = deleteListItem(item)
      root.focus({ preventScroll: true })
      if (backward && previousItem) placeCaretInListItem(previousItem)
      else if (nextTarget instanceof HTMLLIElement) placeCaretInListItem(nextTarget)
      else if (nextTarget) placeCaret(nextTarget, true)
      rememberSelection()
      emitChange('typing')
      return true
    }, [emitChange, rememberSelection])

    const reportCompletedTypedTag = useCallback(() => {
      const root = rootRef.current
      const selection = window.getSelection()
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null
      if (!root || !range?.collapsed || !root.contains(range.startContainer)) return
      const block = closestBlock(range.startContainer, root) ?? root
      if (block !== root && !block.contains(range.startContainer)) return
      const prefix = document.createRange()
      prefix.selectNodeContents(block)
      prefix.setEnd(range.startContainer, range.startOffset)
      const completedTag = extractCompletedInlineTag(prefix.toString().replace(/\u00a0/g, ' '))
      if (completedTag) onInlineTags?.([completedTag])
    }, [onInlineTags])

    const handleInput = (event: FormEvent<HTMLDivElement>) => {
      const inputEvent = event.nativeEvent as InputEvent
      const inputType = inputEvent.inputType ?? ''
      if (!inputEvent.isComposing && transformTypedMarker()) return
      if (inputType.startsWith('delete')) {
        const root = rootRef.current
        const selection = window.getSelection()
        const range = selection?.rangeCount ? selection.getRangeAt(0) : null
        const item = root && range
          ? closestElement(range.startContainer, root)?.closest('li') as HTMLLIElement | null
          : null
        if (item && deleteEmptyChecklistItem(item, inputType.endsWith('Backward'))) return
      }
      rememberSelection()
      if (!inputEvent.isComposing && inputType.startsWith('insert')) reportCompletedTypedTag()
      // Apply the completed inline tag before publishing the body change. The
      // parent can synchronously refresh the draft after onChange, so reversing
      // this order can lose the tag update while the user keeps typing.
      emitChange('typing')
    }

    const handleCompositionEnd = (_event: CompositionEvent<HTMLDivElement>) => {
      if (transformTypedMarker()) return
      reportCompletedTypedTag()
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.nativeEvent.isComposing) return
      if (inlineTagMenuOpen && event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        dismissInlineTagAutocomplete()
        return
      }
      if (inlineTagMenuOpen && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault()
          event.stopPropagation()
          const nextIndex = activeInlineTagIndex < 0
            ? event.key === 'ArrowDown' ? 0 : inlineTagMatches.length - 1
            : (activeInlineTagIndex + (event.key === 'ArrowDown' ? 1 : -1) + inlineTagMatches.length) % inlineTagMatches.length
          setActiveInlineTag({ query: inlineTagAutocomplete!.query, tag: inlineTagMatches[nextIndex]! })
          return
        }
        if (event.key === 'Enter') {
          event.preventDefault()
          event.stopPropagation()
          applyInlineTagSuggestion(inlineTagMatches[Math.max(0, activeInlineTagIndex)]!)
          return
        }
      }
      const shortcut = getEditorShortcut(
        event.key,
        event.metaKey,
        event.ctrlKey,
        event.shiftKey,
        event.altKey,
        event.code,
      )
      if (event.key === 'Tab') {
        const root = rootRef.current
        const selection = window.getSelection()
        const range = selection?.rangeCount ? selection.getRangeAt(0) : null
        if (!root || !range) return
        const item = closestElement(range.startContainer, root)?.closest('li') as HTMLLIElement | null
        event.preventDefault()
        if (item?.parentElement && getListType(item.parentElement)) {
          const changed = event.shiftKey ? outdentListItem(item) : indentListItem(item)
          if (!changed) return
          normalizeEditable(root, previewUrlValues)
          placeCaretInListItem(item)
          rememberSelection()
          emitChange('format')
          scheduleListActionsPosition()
          return
        }
        if (event.shiftKey) {
          if (!outdentTextAtRange(range)) return
        } else {
          indentTextAtRange(range)
        }
        rememberSelection()
        emitChange('typing')
        return
      }
      if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        const root = rootRef.current
        const selection = window.getSelection()
        const range = selection?.rangeCount ? selection.getRangeAt(0) : null
        const item = root && range
          ? closestElement(range.startContainer, root)?.closest('li') as HTMLLIElement | null
          : null
        if (item?.parentElement && getListType(item.parentElement)) {
          event.preventDefault()
          activeListItemRef.current = item
          reorderActiveListItem(event.key === 'ArrowUp' ? -1 : 1)
          return
        }
      }
      if (
        event.key === 'ArrowRight'
        && !event.altKey
        && !event.ctrlKey
        && !event.metaKey
        && !event.shiftKey
      ) {
        const root = rootRef.current
        const selection = window.getSelection()
        const range = selection?.rangeCount ? selection.getRangeAt(0) : null
        const caret = root && range?.collapsed
          ? closestElement(range.startContainer, root)?.closest<HTMLElement>(`[${NOTE_LINK_CARET_ATTRIBUTE}]`)
          : null
        const action = caret?.nextElementSibling
        if (
          caret
          && action instanceof HTMLAnchorElement
          && action.hasAttribute(NOTE_LINK_OPEN_ATTRIBUTE)
          && range
        ) {
          const remainder = document.createRange()
          remainder.selectNodeContents(caret)
          remainder.setStart(range.startContainer, range.startOffset)
          if (!remainder.toString()) {
            event.preventDefault()
            const afterAction = document.createRange()
            afterAction.setStartAfter(action.closest('[data-note-link-unit]') ?? action)
            afterAction.collapse(true)
            selection?.removeAllRanges()
            selection?.addRange(afterAction)
            rememberSelection()
            return
          }
        }
      }
      if (shortcut === 'undo' || shortcut === 'redo') {
        event.preventDefault()
        if (shortcut === 'undo') onUndo()
        else onRedo()
        return
      }
      if (shortcut === 'bold' || shortcut === 'italic' || shortcut === 'underline' || shortcut === 'heading') {
        event.preventDefault()
        applyFormat(shortcut)
        return
      }
      if (shortcut === 'link') {
        event.preventDefault()
        applyFormat('link')
        return
      }
      if (shortcut === 'suppress') {
        event.preventDefault()
        return
      }
      if (shortcut === 'new') {
        event.preventDefault()
        event.stopPropagation()
        // Cancel the shortcut before the new-note action replaces the editor.
        queueMicrotask(onNewNote)
        return
      }
      if (shortcut === 'done') {
        event.preventDefault()
        event.stopPropagation()
        onDone()
        return
      }
      const selectedUnit = rootRef.current?.querySelector<HTMLElement>('[data-note-link-unit][data-selected]')
      if (event.key === 'Backspace' && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        // Beside a link, the first Backspace selects the whole link and the second removes it.
        const root = rootRef.current
        const selection = window.getSelection()
        const range = selection?.rangeCount ? selection.getRangeAt(0) : null
        const unit = root && range ? linkUnitBeforeCaret(root, range) : null
        if (root && unit) {
          event.preventDefault()
          if (selectedUnit === unit) {
            const parent = unit.parentNode!
            const next = document.createRange()
            next.setStart(parent, Array.prototype.indexOf.call(parent.childNodes, unit))
            next.collapse(true)
            unit.remove()
            selection?.removeAllRanges()
            selection?.addRange(next)
            rememberSelection()
            emitChange('typing')
          } else {
            selectedUnit?.removeAttribute('data-selected')
            unit.setAttribute('data-selected', 'true')
          }
          return
        }
      }
      if (selectedUnit && event.key !== 'Shift') selectedUnit.removeAttribute('data-selected')
      if (event.key === 'Backspace' || event.key === 'Delete') {
        const root = rootRef.current
        const selection = window.getSelection()
        const range = selection?.rangeCount ? selection.getRangeAt(0) : null
        const item = root && range?.collapsed
          ? closestElement(range.startContainer, root)?.closest('li') as HTMLLIElement | null
          : null
        const list = item?.parentElement
        const isEmptyChecklistItem = Boolean(
          item
          && list?.tagName === 'UL'
          && list.hasAttribute('data-checklist')
          && !item.textContent?.replace(/\u200B/g, '').trim(),
        )
        if (root && item && isEmptyChecklistItem) {
          event.preventDefault()
          deleteEmptyChecklistItem(item, event.key === 'Backspace')
          return
        }
      }
      if (event.key !== 'Enter') return
      handleEnter(event)
    }

    const handleEnter = (event: Pick<KeyboardEvent<HTMLDivElement>, 'preventDefault' | 'shiftKey' | 'altKey' | 'ctrlKey' | 'metaKey'>) => {
      const root = rootRef.current
      const selection = window.getSelection()
      if (!root || !selection || selection.rangeCount === 0) return
      const range = selection.getRangeAt(0)
      const linkUnit = closestElement(range.startContainer, root)?.closest('[data-note-link-unit]')
      if (range.collapsed && linkUnit && root.contains(linkUnit)) {
        range.setStartAfter(linkUnit)
        range.collapse(true)
      }
      const selectedElement = closestElement(range.startContainer, root)
      const codeBlock = selectedElement?.closest('pre')
      if (codeBlock && root.contains(codeBlock)) {
        event.preventDefault()
        savedRangeRef.current = insertCodeLineBreakAtRange(codeBlock as HTMLElement, range)
        rememberSelection()
        emitChange('typing')
        return
      }
      const quote = selectedElement?.closest('blockquote')
      const item = selectedElement?.closest('li')
      if (
        (event.shiftKey || (!item && !quote))
        && !event.altKey
        && !event.ctrlKey
        && !event.metaKey
        && !selectedElement?.closest('h1, h2, h3, h4, h5, h6')
      ) {
        const softBreakScope = item ?? quote ?? selectedElement?.closest('p, div') ?? root
        if (root.contains(softBreakScope) && softBreakScope.contains(range.endContainer)) {
          event.preventDefault()
          savedRangeRef.current = insertSoftLineBreakAtRange(softBreakScope, range)
          normalizeEditable(root, previewUrlValues)
          rememberSelection()
          emitChange('typing')
          return
        }
      }
      if (quote && root.contains(quote)) {
        event.preventDefault()
        const paragraph = exitQuote(quote)
        placeCaret(paragraph, true)
        rememberSelection()
        emitChange('typing')
        return
      }
      const list = item?.parentElement
      if (!item || (list?.tagName !== 'UL' && list?.tagName !== 'OL')) return
      event.preventDefault()
      const hasText = Boolean(item.textContent?.replace(/\u200B/g, '').trim())
      if (!hasText) {
        const paragraph = document.createElement('p')
        paragraph.append(document.createElement('br'))
        list.after(paragraph)
        item.remove()
        if (!list.children.length) list.remove()
        placeCaret(paragraph, true)
      } else if (
        list.hasAttribute('data-checklist')
        && Boolean(item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')?.checked)
        && isCaretAtStartOfListItem(item, range)
      ) {
        const newItem = document.createElement('li')
        newItem.append(document.createElement('br'))
        const checkbox = document.createElement('input')
        checkbox.type = 'checkbox'
        newItem.append(checkbox)
        item.before(newItem)
        normalizeEditable(root, previewUrlValues)
        placeCaretAtStartOfListItem(newItem)
      } else if (
        list.hasAttribute('data-checklist')
        && item === list.lastElementChild
        && Boolean(item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')?.checked)
      ) {
        // Completed terminal tasks are an end-of-list cue, not an invitation to
        // create another unchecked task.
        placeCaret(ensureEditableParagraphAfterList(list), true)
      } else {
        const startsItem = isCaretAtStartOfListItem(item, range)
        const nextItem = splitListItem(item, range, list.hasAttribute('data-checklist'))
        normalizeEditable(root, previewUrlValues)
        if (startsItem) placeCaretAtStartOfListItem(item)
        else placeCaretAtStartOfListItem(nextItem)
      }
      rememberSelection()
      emitChange('typing')
    }

    // Android IMEs can emit beforeinput without an Enter keydown. React's
    // synthetic beforeinput does not expose the native inputType reliably.
    useEffect(() => {
      const root = rootRef.current
      if (!root) return
      const beforeInput = (event: InputEvent) => {
        if (!event.cancelable || event.isComposing || event.defaultPrevented) return
        if (event.inputType.startsWith('delete')) {
          const selection = window.getSelection()
          const range = selection?.rangeCount ? selection.getRangeAt(0) : null
          const item = range?.collapsed && root.contains(range.startContainer)
            ? closestElement(range.startContainer, root)?.closest('li') as HTMLLIElement | null
            : null
          if (item && deleteEmptyChecklistItem(item, event.inputType.endsWith('Backward'))) {
            event.preventDefault()
          }
          return
        }
        if (event.inputType !== 'insertParagraph' && event.inputType !== 'insertLineBreak') return
        handleEnter({
          preventDefault: () => event.preventDefault(),
          shiftKey: event.inputType === 'insertLineBreak',
          altKey: false, ctrlKey: false, metaKey: false,
        })
      }
      root.addEventListener('beforeinput', beforeInput)
      return () => root.removeEventListener('beforeinput', beforeInput)
    })

    const handleCopy = (event: ClipboardEvent<HTMLDivElement>) => {
      const root = rootRef.current
      const selection = window.getSelection()
      if (!root || !selection?.rangeCount) return
      const range = selection.getRangeAt(0)
      // Selection.toString preserves rendered paragraph breaks in plain text.
      const payload = editorSelectionForClipboard(root, range, selection.toString())
      if (!payload) return
      event.clipboardData.clearData()
      event.clipboardData.setData('text/plain', payload.text)
      if (payload.html) event.clipboardData.setData('text/html', payload.html)
      event.preventDefault()
      if (event.type === 'cut') {
        // Keep native range deletion/merging, then publish one undoable edit.
        if (!document.execCommand('delete')) range.deleteContents()
        emitChange('format')
        rememberSelection()
      }
    }

    const insertHtml = useCallback(
      (html: string) => {
        const root = rootRef.current
        const range = ensureRange()
        if (root && range && insertPastedEditorHtml(root, range, html)) {
          normalizeEditable(root, previewUrlValues)
          rememberSelection()
          emitChange('typing')
          return
        }
        document.execCommand('insertHTML', false, html)
        if (root) normalizeEditable(root, previewUrlValues)
        rememberSelection()
        emitChange('typing')
      },
      [emitChange, ensureRange, previewUrlValues, rememberSelection],
    )

    const insertClipboardPayload = useCallback((richHtml: string, text: string) => {
      const normalizedText = text.replace(/\r\n?/g, '\n')
      const sourceMarkdown = richHtml ? pastedHtmlToMarkdown(richHtml) : normalizedText
      const formattedMarkdown = formatNoteUrlsAsMarkdown(sourceMarkdown)
      const reportInsertedTags = () => {
        const pastedTags = extractInlineTags(sourceMarkdown)
        if (pastedTags.length > 0) onInlineTags?.(pastedTags)
      }
      if (richHtml) {
        insertHtml(markdownToEditorHtml(formattedMarkdown))
        reportInsertedTags()
      } else if (looksLikeMarkdown(text)) {
        insertHtml(markdownToEditorHtml(formattedMarkdown))
        reportInsertedTags()
      } else if (formattedMarkdown !== normalizedText) {
        insertHtml(markdownToEditorHtml(formattedMarkdown))
        reportInsertedTags()
      } else {
        const root = rootRef.current
        const range = ensureRange()
        if (root && range && normalizedText.includes('\n')) {
          const blocks = document.createElement('div')
          for (const line of normalizedText.split('\n')) {
            const paragraph = document.createElement('p')
            if (line) paragraph.textContent = line
            else paragraph.append(document.createElement('br'))
            blocks.append(paragraph)
          }
          if (insertPastedEditorHtml(root, range, blocks.innerHTML)) {
            normalizeEditable(root, previewUrlValues)
            rememberSelection()
            emitChange('typing')
            reportInsertedTags()
            return
          }
        }
        document.execCommand('insertText', false, text)
        rememberSelection()
        emitChange('typing')
        reportInsertedTags()
      }
    }, [emitChange, ensureRange, insertHtml, onInlineTags, previewUrlValues, rememberSelection])

    const captureNativePaste = useCallback((plainText: string): boolean => {
      const root = rootRef.current
      const range = ensureRange()
      const host = root?.parentElement
      if (!root || !range || !host) return false

      pasteCaptureCleanupRef.current?.()
      const savedRange = range.cloneRange()
      const capture = document.createElement('div')
      capture.contentEditable = 'true'
      capture.dataset.editorPasteCapture = 'true'
      capture.spellcheck = false
      capture.style.cssText = 'position: fixed; left: -10000px; top: 10px; width: 1px; height: 1px; overflow: hidden;'
      host.append(capture)

      let timeoutId: number | null = null
      let finished = false
      const cleanup = () => {
        if (timeoutId !== null) window.clearTimeout(timeoutId)
        capture.removeEventListener('input', finalize)
        capture.remove()
        if (pasteCaptureCleanupRef.current === cleanup) pasteCaptureCleanupRef.current = null
      }
      const finalize = () => {
        if (finished) return
        finished = true
        const capturedHtml = capture.innerHTML
        const capturedText = capture.textContent ?? plainText
        cleanup()

        root.focus({ preventScroll: true })
        const selection = window.getSelection()
        if (root.contains(savedRange.commonAncestorContainer)) {
          selection?.removeAllRanges()
          selection?.addRange(savedRange)
          savedRangeRef.current = savedRange.cloneRange()
        } else {
          placeCaretAtEndOfEditor(root)
          rememberSelection()
        }
        insertClipboardPayload(capturedHtml, capturedText || plainText)
      }

      capture.addEventListener('input', finalize, { once: true })
      pasteCaptureCleanupRef.current = cleanup
      capture.focus({ preventScroll: true })
      timeoutId = window.setTimeout(
        finalize,
        APP_SETTINGS.interactions.richPasteCaptureFallbackMs,
      )
      return true
    }, [ensureRange, insertClipboardPayload, rememberSelection])

    useEffect(() => () => pasteCaptureCleanupRef.current?.(), [])

    useLayoutEffect(() => {
      if (!pendingLinkPaste) return
      const dialog = pasteDialogRef.current
      if (!dialog) return
      if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal()
      else dialog.setAttribute('open', '')
      return () => {
        if (!dialog.open) return
        if (typeof dialog.close === 'function') dialog.close()
        else dialog.removeAttribute('open')
      }
    }, [pendingLinkPaste])

    const pasteLeaving = useRef(false)
    // The paste dialog's own buttons close it through the sheet exit, then apply the choice.
    const leavePaste = (attach: boolean) => {
      if (pasteLeaving.current) return
      pasteLeaving.current = true
      leaveSheet(pasteDialogRef.current, () => { pasteLeaving.current = false; resolveLinkPaste(attach) })
    }

    const resolveLinkPaste = (attach: boolean) => {
      const pending = pendingLinkPaste
      if (!pending) return
      const dialog = pasteDialogRef.current
      if (dialog?.open) {
        if (typeof dialog.close === 'function') dialog.close()
        else dialog.removeAttribute('open')
      }
      setPendingLinkPaste(null)
      const root = rootRef.current
      if (!root) return
      root.focus({ preventScroll: true })
      const selection = window.getSelection()
      if (root.contains(pending.range.commonAncestorContainer)) {
        selection?.removeAllRanges()
        selection?.addRange(pending.range)
        savedRangeRef.current = pending.range.cloneRange()
      } else {
        placeCaretAtEndOfEditor(root)
        rememberSelection()
      }
      onPasteLinkAttachments?.(pending.urls, attach)
      insertClipboardPayload(pending.richHtml, pending.text)
    }

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
      const pastedImages = onPasteImages
        ? clipboardImageFiles(event.clipboardData)
        : []
      if (pastedImages.length > 0) {
        event.preventDefault()
        onPasteImages?.(pastedImages)
        return
      }
      const richHtml = event.clipboardData.getData('text/html')
      const text = event.clipboardData.getData('text/plain')
      const pastedContent = richHtml ? pastedHtmlToMarkdown(richHtml) : text
      const urlCount = countUrlOccurrences(pastedContent)
      if (onPasteLinkAttachments && urlCount > APP_SETTINGS.links.bulkPasteAttachmentPromptThreshold) {
        event.preventDefault()
        const range = ensureRange()
        if (range) setPendingLinkPaste({ richHtml, text, urls: extractUrls(pastedContent), urlCount, range: range.cloneRange() })
        return
      }
      if (richHtml || looksLikeMarkdown(text) || /^https?:\/\/\S+$/i.test(text.trim())) {
        event.preventDefault()
        insertClipboardPayload(richHtml, text)
        return
      }
      if (!text || captureNativePaste(text)) return
      event.preventDefault()
      insertClipboardPayload('', text)
    }

    const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        onEditorFocusChange?.(true)
      }
    }

    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
      rememberSelection()
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        onEditorFocusChange?.(false)
        if (!inlineTagMenuRef.current?.contains(event.relatedTarget as Node | null)) dismissInlineTagAutocomplete()
      }
      const root = rootRef.current
      if (root && healEditableUrlLabels(root, previewUrls, false)) emitChange('format')
    }

    // Native editing can leave prose as direct text children (for example before
    // a newly formatted PRE). Text hits can target the root, just like canvas.
    const hitsRootText = (root: HTMLElement, x: number, y: number) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
      while (walker.nextNode()) {
        const node = walker.currentNode
        if (!node.textContent?.trim()) continue
        if (node.parentElement?.closest('button, input, textarea, [contenteditable="false"]')) continue
        const range = document.createRange()
        range.selectNodeContents(node)
        if ([...(range.getClientRects?.() ?? [])].some(rect =>
          x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom)) return true
      }
      return false
    }

    const rightSideLineCaret = (root: HTMLElement, target: EventTarget | null, x: number, y: number): Range | null => {
      if (!(target instanceof HTMLElement) || !root.contains(target)) return null
      const block = target === root ? root : closestBlock(target, root)
      if (!block) return null
      const nodes = [...(function* () {
        const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT)
        while (walker.nextNode()) {
          const node = walker.currentNode
          if (node.nodeType === Node.TEXT_NODE || node.nodeName === 'BR') yield node
        }
      })()]
      const lineHeight = Number.parseFloat(getComputedStyle(block).lineHeight)
      let lineRight = -Infinity
      for (const node of nodes) {
        if (node.nodeType === Node.TEXT_NODE && !node.textContent?.length) continue
        if (node.parentElement?.closest('button, input, textarea, [contenteditable="false"]')) continue
        const range = document.createRange()
        if (node.nodeName === 'BR') range.selectNode(node)
        else range.selectNodeContents(node)
        for (const rect of [...(range.getClientRects?.() ?? [])]) {
          const halfLeading = Number.isFinite(lineHeight) ? Math.max(0, (lineHeight - rect.height) / 2) : 0
          if (y >= rect.top - halfLeading && y <= rect.bottom + halfLeading) {
            lineRight = Math.max(lineRight, rect.right)
          }
        }
      }
      if (!Number.isFinite(lineRight) || x <= lineRight + 1) return null
      let caret = document.caretRangeFromPoint?.(x, y)
      if (!caret) {
        const position = document.caretPositionFromPoint?.(x, y)
        if (position) {
          caret = document.createRange()
          caret.setStart(position.offsetNode, position.offset)
          caret.collapse(true)
        }
      }
      if (!caret || !block.contains(caret.startContainer)) return null
      return caret
    }

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target
      const root = rootRef.current
      if (target === root && root) {
        if (hitsRootText(root, event.clientX, event.clientY)) {
          rememberSelection()
          return
        }
        const sameLineCaret = rightSideLineCaret(root, target, event.clientX, event.clientY)
        if (sameLineCaret) {
          event.preventDefault()
          root.focus({ preventScroll: true })
          const selection = window.getSelection()
          selection?.removeAllRanges()
          selection?.addRange(sameLineCaret)
          rememberSelection()
          return
        }
        const selection = window.getSelection()
        const range = selection?.rangeCount ? selection.getRangeAt(0) : null
        if (range && !range.collapsed && root.contains(range.commonAncestorContainer)) {
          event.preventDefault()
          rememberSelection()
          return
        }
        event.preventDefault()
        root.focus({ preventScroll: true })
        placeCaretAtEndOfEditor(root)
        rememberSelection()
        return
      }
      // The ⋯ at the end of a row, or anywhere else on a pill or a row (label, padding, row body), opens the link menu.
      // A tap never moves the caret into the label or edits it; the open action keeps its own handler.
      const tappedUnit = target instanceof Element ? target.closest<HTMLElement>('[data-note-link-unit]') : null
      const moreButton = target instanceof Element ? target.closest<HTMLElement>('[data-note-link-action]') : null
      const linkUnit = target instanceof Element && !target.closest(`[${NOTE_LINK_OPEN_ATTRIBUTE}]`) ? tappedUnit : null
      const labelLink = linkUnit?.querySelector<HTMLAnchorElement>(`:scope > a[data-note-link-display]`) ?? null
      if (labelLink && linkUnit && root?.contains(labelLink)) {
        event.preventDefault()
        if (moreButton) event.stopPropagation()
        setMenuAnchor((current) => current === labelLink ? null : labelLink)
        return
      }
      const openAction = target instanceof Element
        ? target.closest<HTMLAnchorElement>(`a[${NOTE_LINK_OPEN_ATTRIBUTE}]`)
        : null
      if (openAction) {
        event.preventDefault()
        event.stopPropagation()
        const href = openAction.getAttribute('href')
        if (href) {
          const opened = window.open(href, '_blank', 'noopener,noreferrer')
          if (opened) opened.opener = null
        }
        return
      }
      if (target instanceof HTMLInputElement && target.type === 'checkbox') {
        const item = target.closest('li')
        const list = item?.parentElement
        if (item && list?.hasAttribute('data-checklist')) {
          const root = rootRef.current
          const uncheckedSelection = !target.checked && root
            ? pendingCheckboxSelectionRef.current
              ?? snapshotEditorSelection(root, savedRangeRef.current)
            : null
          pendingCheckboxSelectionRef.current = null
          const nextItem = item.nextElementSibling instanceof HTMLLIElement
            ? item.nextElementSibling
            : null
          const items = [...list.children].filter((child): child is HTMLLIElement => child instanceof HTMLLIElement)
          const previousTops = new Map(items.map((candidate) => [candidate, candidate.getBoundingClientRect().top]))
          reorderChecklistItem(item, target.checked)
          animateChecklistReorder(items, previousTops)
          scheduleListActionsPosition()
          emitChange('format')
          root?.focus({ preventScroll: true })
          if (target.checked) {
            const nextTarget = nextItem ?? ensureEditableParagraphAfterList(list)
            if (nextTarget instanceof HTMLLIElement) placeCaretInListItem(nextTarget)
            else placeCaret(nextTarget, true)
          } else if (!root || !restoreEditorSelection(root, uncheckedSelection)) {
            placeCaretInListItem(item)
          }
          rememberSelection()
          return
        }
        emitChange('format')
        return
      }
      if (root) {
        const sameLineCaret = rightSideLineCaret(root, target, event.clientX, event.clientY)
        if (sameLineCaret) {
          event.preventDefault()
          root.focus({ preventScroll: true })
          const selection = window.getSelection()
          selection?.removeAllRanges()
          selection?.addRange(sameLineCaret)
          rememberSelection()
          return
        }
      }
      if (target instanceof HTMLAnchorElement) event.preventDefault()
    }

    // A tap on a pill or row only reveals its actions: it must not move the caret, focus the editor or start a selection.
    const keepCaretOnLinkTap = (event: PointerEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>): boolean => {
      const root = rootRef.current
      if (!root || !(event.target instanceof Node) || !dressedLinkUnit(event.target, root)) return false
      event.preventDefault()
      return true
    }
    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
      const target = event.target
      const root = rootRef.current
      if (keepCaretOnLinkTap(event)) return
      if (target === root && root && !hitsRootText(root, event.clientX, event.clientY)) {
        // Android delivers pointerdown before it claims an edge swipe for Back.
        // Suppress native focus here; only a completed click requests text entry.
        event.preventDefault()
        return
      }
      pendingCheckboxSelectionRef.current =
        target instanceof HTMLInputElement
        && target.type === 'checkbox'
        && root
          ? snapshotEditorSelection(root, savedRangeRef.current)
          : null
    }

    return (
      <>
        <div
          ref={rootRef}
          className="note-editor wysiwyg-editor formatted-note-content"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label="Note text"
          aria-multiline="true"
          aria-autocomplete="list"
          aria-controls={inlineTagMenuOpen ? inlineTagListboxId : undefined}
          aria-activedescendant={inlineTagMenuOpen && activeInlineTagIndex >= 0 ? `${inlineTagListboxId}-${activeInlineTagIndex}` : undefined}
          data-placeholder={placeholder}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onClick={handleClick}
          onInput={handleInput}
          onCompositionEnd={handleCompositionEnd}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onCopy={handleCopy}
          onCut={handleCopy}
          onPointerDown={handlePointerDown}
          onMouseDown={keepCaretOnLinkTap}
          onPointerUp={rememberSelection}
          onKeyUp={rememberSelection}
          spellCheck
        />
        {inlineTagAutocomplete && inlineTagMatches.length > 0
          ? createPortal(
              <div
                ref={inlineTagFrameRef}
                className="inline-tag-autocomplete-viewport"
              >
                <div
                  ref={inlineTagMenuRef}
                  id={inlineTagListboxId}
                  className="card card-border inline-tag-autocomplete"
                  role="listbox"
                  aria-label={`Tag suggestions for ${inlineTagAutocomplete.query}`}
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape' || event.nativeEvent.isComposing) return
                    event.preventDefault()
                    event.stopPropagation()
                    dismissInlineTagAutocomplete()
                    rootRef.current?.focus({ preventScroll: true })
                  }}
                >
                  <ul className="menu menu-sm menu-vertical inline-tag-autocomplete__menu">
                    {inlineTagMatches.map((tag, index) => (
                      <li key={tag} role="presentation" className="min-w-0 w-full">
                        <button
                          id={`${inlineTagListboxId}-${index}`}
                          type="button"
                          tabIndex={-1}
                          className={`btn btn-ghost inline-tag-autocomplete__option${index === activeInlineTagIndex ? ' menu-active' : ''}`}
                          role="option"
                          aria-selected={index === activeInlineTagIndex}
                          title={`#${tag}`}
                          onPointerDown={(event) => {
                            if (event.pointerType === 'mouse') event.preventDefault()
                          }}
                          // Touch must retain its native scroll/click stream, but
                          // its compatibility mousedown must not blur the editor
                          // and unmount this option before the click can apply it.
                          onMouseDown={(event) => event.preventDefault()}
                          onPointerMove={(event) => {
                            if (event.pointerType === 'mouse') setActiveInlineTag({ query: inlineTagAutocomplete.query, tag })
                          }}
                          onClick={() => applyInlineTagSuggestion(tag)}
                        >
                          <span className="inline-tag-autocomplete__prefix" aria-hidden="true">#</span>
                          <span className="min-w-0 truncate">{tag}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>,
              document.body,
            )
          : null}
        {listActions
          ? createPortal(
              <div
                ref={listActionsRef}
                className="list-item-actions"
                data-expanded={listActionsExpanded}
                role="toolbar"
                aria-label="List item actions"
              >
                <button
                  className="list-item-actions__reveal"
                  type="button"
                  aria-label="Show list item actions"
                  aria-expanded={listActionsExpanded}
                  title="Show list item actions"
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={openListActions}
                >
                  <Icon name="list-reorder" strokeWidth={2.8} />
                </button>
                <button
                  className="list-item-actions__action"
                  type="button"
                  disabled={!listActions.canMoveUp}
                  aria-label="Move list item up"
                  title="Move up (Alt+Up)"
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={() => reorderActiveListItem(-1)}
                >
                  <Icon name="arrow-up" strokeWidth={2.8} />
                </button>
                <button
                  className="list-item-actions__action"
                  type="button"
                  disabled={!listActions.canMoveDown}
                  aria-label="Move list item down"
                  title="Move down (Alt+Down)"
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={() => reorderActiveListItem(1)}
                >
                  <Icon name="arrow-down" strokeWidth={2.8} />
                </button>
                <button
                  type="button"
                  className="list-item-actions__action list-item-actions__delete"
                  aria-label="Delete list item"
                  title="Delete list item"
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={deleteActiveListItem}
                >
                  <Icon name="trash" />
                </button>
                <button
                  type="button"
                  className="list-item-actions__action list-item-actions__close"
                  aria-label="Close list item actions"
                  title="Close list item actions"
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={() => setListActionsExpanded(false)}
                >
                  <Icon name="close" strokeWidth={2.4} />
                </button>
              </div>,
              document.body,
            )
          : null}
        {menuAnchor
          ? (() => {
              const entity = previewUrlValues.urlByTarget?.get(linkTargetKey(menuAnchor.getAttribute('href') ?? '')) ?? null
              return <LinkUnitMenu
                anchor={menuAnchor}
                entity={entity}
                extras={entity ? linkActions?.(entity) : undefined}
                onClose={closeLinkMenu}
                onEdit={editLinkAnchor}
                onRetryMetadata={onRetryLinkMetadata}
                onDisplay={onLinkDisplay}
                onRemoveLink={onRemoveLink ? removeLinkAnchor : undefined}
              />
            })()
          : null}
        {linkOpen
          ? createPortal(
              <dialog
                ref={linkDialogRef}
                className="modal modal-bottom sm:modal-middle studio-sheet link-dialog-modal"
                aria-label={existingLinkRef.current ? 'Edit link' : 'Add link'}
                onCancel={(event) => {
                  event.preventDefault()
                  leaveLinkDialog()
                }}
                onKeyDownCapture={(event) => {
                  if (event.key !== 'Escape') return
                  event.preventDefault()
                  event.stopPropagation()
                  leaveLinkDialog()
                }}
                onClick={(event) => {
                  if (event.target === event.currentTarget) leaveLinkDialog()
                }}
              >
                <form
                  className="modal-box sheet-panel link-dialog"
                  noValidate
                  onSubmit={submitLink}
                >
                  <header className="sheet-header">
                    <h2 className="sheet-title"><Icon name="link" />{existingLinkRef.current ? 'Edit link' : 'Add link'}</h2>
                    <button className="btn btn-ghost btn-square sheet-close" type="button" onClick={leaveLinkDialog} aria-label="Close link editor"><Icon name="close" /></button>
                  </header>
                  <div className="sheet-body">
                    <div className="sheet-field">
                      <label htmlFor="note-link-text">Text</label>
                      <input
                        ref={linkTitleInputRef}
                        id="note-link-text"
                        className="input"
                        autoFocus
                        value={linkDraft.text}
                        onChange={(event) => setLinkDraft((current) => ({ ...current, text: event.target.value }))}
                        placeholder="Link text"
                      />
                    </div>
                    <div className="sheet-field">
                      <label htmlFor="note-link-url">URL</label>
                      <input
                        id="note-link-url"
                        className="input"
                        type="url"
                        inputMode="url"
                        value={linkDraft.url}
                        aria-describedby={linkError ? 'note-link-url-error' : undefined}
                        aria-invalid={linkError ? true : undefined}
                        onChange={(event) => {
                          setLinkDraft((current) => ({ ...current, url: event.target.value }))
                          if (linkError) setLinkError(null)
                        }}
                        placeholder="https://…"
                      />
                    </div>
                    {linkError ? <p id="note-link-url-error" className="form-validation-message" role="alert">{linkError}</p> : null}
                  </div>
                  <div className="sheet-footer">
                    {existingLinkRef.current ? (
                      <button className="btn btn-error link-dialog__remove" type="button" onClick={removeLink}>Remove link</button>
                    ) : null}
                    <button className="btn btn-primary link-dialog__submit" type="submit">Apply link</button>
                  </div>
                </form>
                <form method="dialog" className="modal-backdrop" onSubmit={(event) => {
                  event.preventDefault()
                  leaveLinkDialog()
                }}><button>Close link editor</button></form>
              </dialog>,
              document.body,
            )
          : null}
        {pendingLinkPaste
          ? createPortal(
              <dialog
                ref={pasteDialogRef}
                className="modal modal-bottom sm:modal-middle studio-sheet link-dialog-modal"
                aria-label="Paste links"
                onCancel={(event) => { event.preventDefault(); leavePaste(false) }}
              >
                <div className="modal-box sheet-panel link-dialog">
                  <header className="sheet-header">
                    <h2 className="sheet-title"><Icon name="link" />Paste links</h2>
                  </header>
                  <div className="sheet-body"><p className="sheet-note">This paste contains {pendingLinkPaste.urlCount} links{pendingLinkPaste.urlCount === pendingLinkPaste.urls.length ? '' : ` (${pendingLinkPaste.urls.length} unique)`}. Turn them into attachments with previews?</p></div>
                  <div className="sheet-footer">
                    <button className="btn" type="button" onClick={() => leavePaste(false)}>Paste text only</button>
                    <button className="btn btn-primary" type="button" onClick={() => leavePaste(true)}>Add {pendingLinkPaste.urls.length} {pendingLinkPaste.urls.length === 1 ? 'attachment' : 'attachments'}</button>
                  </div>
                </div>
              </dialog>,
              document.body,
            )
          : null}
      </>
    )
  },
)

export const MarkdownEditor = memo(MarkdownEditorImpl)
